import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type { UnitConversionListResponse, CreateUnitConversionInput, UpdateUnitConversionInput, UpdateUnitConversionStatusInput, UnitConversionErrorCode, ApiEnvelope } from "@/domain/types";
import axios from "axios";

export function createUnitConversionError(code: UnitConversionErrorCode): Error {
  const err = new Error(code);
  err.name = "UnitConversionError";
  return err;
}

export function isUnitConversionError(err: unknown): err is Error & { message: UnitConversionErrorCode } {
  return err instanceof Error && err.name === "UnitConversionError";
}

function mapError(statusCode: number, data?: unknown): UnitConversionErrorCode {
  if (statusCode === 409) return "DUPLICATE";
  if (statusCode === 400) {
    const msg = typeof data === "object" && data !== null ? (data as Record<string, unknown>).message : "";
    if (typeof msg === "string") {
      if (msg.includes("same")) return "SAME_UNIT";
      if (msg.includes("positive") || msg.includes("factor")) return "INVALID_FACTOR";
    }
    return "VALIDATION";
  }
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listUnitConversions(params: { page?: number; limit?: number; status?: string }): Promise<UnitConversionListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.status) query.set("status", params.status);
  const response = await warehouseClient.get<ApiEnvelope<UnitConversionListResponse>>(`/unit-conversion?${query.toString()}`);
  return response.data.data;
}

export async function createUnitConversion(input: CreateUnitConversionInput): Promise<void> {
  try {
    await warehouseClient.post("/unit-conversion", input);
  } catch (err) {
    const code = extractStatusCode(err);
    const data = axios.isAxiosError(err) ? err.response?.data : undefined;
    throw createUnitConversionError(mapError(code, data));
  }
}

export async function updateUnitConversion(input: UpdateUnitConversionInput): Promise<void> {
  try {
    await warehouseClient.patch("/unit-conversion", input);
  } catch (err) {
    throw createUnitConversionError(mapError(extractStatusCode(err)));
  }
}

export async function updateUnitConversionStatus(input: UpdateUnitConversionStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/unit-conversion/status", input);
  } catch (err) {
    throw createUnitConversionError(mapError(extractStatusCode(err)));
  }
}
